/**
 * File upload utilities for processing images, PDFs, and documents
 */

export interface ProcessedFile {
  name: string;
  type: 'image' | 'pdf' | 'document';
  content: string; // Base64 for images, extracted text for docs
  size: number;
  previewUrl?: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];

export async function processUploadedFile(file: File): Promise<ProcessedFile> {
  // Validate file type
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error(`File type not supported. Allowed: Images, PDFs, and Documents`);
  }

  // Validate file size
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File too large. Maximum size: 10MB`);
  }

  const fileName = file.name;
  const fileType = getFileType(file.type);

  try {
    if (fileType === 'image') {
      return await processImage(file, fileName);
    } else if (fileType === 'pdf') {
      return await processPDF(file, fileName);
    } else if (fileType === 'document') {
      return await processDocument(file, fileName);
    }
  } catch (error) {
    throw new Error(`Failed to process file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  throw new Error('Unsupported file type');
}

function getFileType(mimeType: string): 'image' | 'pdf' | 'document' {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType.includes('word') || mimeType === 'text/plain') return 'document';
  throw new Error('Unknown file type');
}

async function processImage(file: File, name: string): Promise<ProcessedFile> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      resolve({
        name,
        type: 'image',
        content: base64,
        size: file.size,
        previewUrl: base64,
      });
    };
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}

async function processPDF(file: File, name: string): Promise<ProcessedFile> {
  // For now, we'll read PDF as base64 and let the backend handle extraction
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      resolve({
        name,
        type: 'pdf',
        content: base64,
        size: file.size,
      });
    };
    reader.onerror = () => reject(new Error('Failed to read PDF file'));
    reader.readAsDataURL(file);
  });
}

async function processDocument(file: File, name: string): Promise<ProcessedFile> {
  // For text files, extract content directly
  if (file.type === 'text/plain') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        resolve({
          name,
          type: 'document',
          content: text,
          size: file.size,
        });
      };
      reader.onerror = () => reject(new Error('Failed to read text file'));
      reader.readAsText(file);
    });
  }

  // For Word docs, read as base64 and let backend handle
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      resolve({
        name,
        type: 'document',
        content: base64,
        size: file.size,
      });
    };
    reader.onerror = () => reject(new Error('Failed to read document file'));
    reader.readAsDataURL(file);
  });
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
